import Image from '@tiptap/extension-image'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { isValidImage, createPreviewUrl } from '@/lib/imageUpload'

export interface ImageUploadHandler {
  (file: File): Promise<string> // Returns the URL of the uploaded image
}

export interface MacroImageOptions {
  onUpload?: ImageUploadHandler
  onUploadStart?: () => void
  onUploadEnd?: () => void
  onUploadError?: (error: Error) => void
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    macroImage: {
      /**
       * Insert an image by URL
       */
      insertImage: (url: string, alt?: string) => ReturnType
      /**
       * Upload and insert an image file
       */
      uploadImage: (file: File) => ReturnType
    }
  }
}

export const MacroImageExtension = Image.extend<MacroImageOptions>({
  name: 'image',

  addOptions() {
    return {
      ...this.parent?.(),
      onUpload: undefined,
      onUploadStart: undefined,
      onUploadEnd: undefined,
      onUploadError: undefined,
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      loading: {
        default: false,
        renderHTML: (attributes) => {
          if (!attributes.loading) return {}
          return {
            class: 'opacity-50',
            'data-loading': 'true',
          }
        },
      },
    }
  }
  ,

  addCommands() {
    return {
      ...this.parent?.(),
      insertImage:
        (url: string, alt?: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { src: url, alt: alt || '' },
          })
        },
      uploadImage:
        (file: File) =>
        ({ editor, commands }) => {
          const { onUpload, onUploadStart, onUploadEnd, onUploadError } = this.options

          if (!isValidImage(file)) {
            onUploadError?.(new Error('Invalid image file'))
            return false
          }

          // Create a temporary preview URL
          const previewUrl = createPreviewUrl(file)

          // Insert image with loading state
          const inserted = commands.insertContent({
            type: this.name,
            attrs: { src: previewUrl, alt: file.name, loading: true },
          })

          if (!inserted) return false

          // Upload the image if handler is provided
          if (onUpload) {
            onUploadStart?.()

            onUpload(file)
              .then((url) => {
                // Replace preview URL with actual URL
                const { state } = editor
                const { doc } = state
                let imagePos: number | null = null

                doc.descendants((node, pos) => {
                  if (node.type.name === 'image' && node.attrs.src === previewUrl) {
                    imagePos = pos
                    return false
                  }
                })

                if (imagePos !== null) {
                  editor
                    .chain()
                    .focus()
                    .command(({ tr }) => {
                      tr.setNodeMarkup(imagePos!, undefined, {
                        src: url,
                        alt: file.name,
                        loading: false,
                      })
                      return true
                    })
                    .run()
                }

                onUploadEnd?.()
              })
              .catch((error) => {
                // Remove the image on error
                const { state } = editor
                const { doc } = state
                let imagePos: number | null = null

                doc.descendants((node, pos) => {
                  if (node.type.name === 'image' && node.attrs.src === previewUrl) {
                    imagePos = pos
                    return false
                  }
                })

                if (imagePos !== null) {
                  editor
                    .chain()
                    .focus()
                    .command(({ tr }) => {
                      tr.delete(imagePos!, imagePos! + 1)
                      return true
                    })
                    .run()
                }

                onUploadError?.(error)
                onUploadEnd?.()
              })
          }

          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor

    return [
      new Plugin({
        key: new PluginKey('macroImageDrop'),
        props: {
          handleDrop: (_view, event, _slice, moved) => {
            if (moved || !event.dataTransfer?.files.length) {
              return false
            }

            const files = Array.from(event.dataTransfer.files)
            const images = files.filter((file) => isValidImage(file))

            if (images.length === 0) return false

            event.preventDefault()

            images.forEach((file) => {
              editor.commands.uploadImage(file)
            })

            return true
          },
          handlePaste: (_view, event) => {
            const items = event.clipboardData?.items
            if (!items) return false

            const images: File[] = []

            for (const item of Array.from(items)) {
              if (item.type.startsWith('image/')) {
                const file = item.getAsFile()
                if (file && isValidImage(file)) {
                  images.push(file)
                }
              }
            }

            if (images.length === 0) return false

            event.preventDefault()

            images.forEach((file) => {
              editor.commands.uploadImage(file)
            })

            return true
          },
        },
      }),
    ]
  },
})

export default MacroImageExtension
